import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Code, Loader2, Pencil, Plus, X, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import {
  CUSTOM_EVENT_CONVERSION_TYPES,
  CUSTOM_EVENT_DISPLAY_TYPES,
  CUSTOM_EVENT_VALUE_TYPES,
  CustomEventTypesApi,
  customEventExampleCodes,
  type CustomEventConversionType,
  type CustomEventDisplayType,
  type CustomEventType,
  type CustomEventTypeInput,
  type CustomEventValueType,
} from '@/services/networks/customEventTypesApi'
import { AppShell } from '../../components/AppShell'
import { CopyableCode } from '../../components/CopyableCode'
import { DetailList, DetailRow } from '../../components/DetailList'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useEditMode } from '../../hooks/useEditMode'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

// ── Inner form component ────────────────────────────────────────────────────

interface CustomEventEditFormProps {
  customEvent: CustomEventType | null
  id: string
  isNew: boolean
}

function CustomEventEditForm({ customEvent, id, isNew }: CustomEventEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)

  const form = useForm({
    defaultValues: {
      name: customEvent?.name ?? '',
      description: customEvent?.description ?? '',
      valueType: (customEvent?.valueType ?? '') as CustomEventValueType | '',
      type: (customEvent?.type ?? 'COUNT') as CustomEventDisplayType,
      conversionType: (customEvent?.conversionType ?? null) as CustomEventConversionType | null,
      priority: (customEvent?.priority ?? 0) as number | null,
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        valueType: value.valueType || undefined,
        type: value.type,
        conversionType: value.conversionType,
        priority: value.priority ?? 0,
        objectState: (customEvent?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => CustomEventTypesApi.update(id, { name: customEvent?.name ?? '', objectState }),
    ['custom-event', id],
    ['custom-events']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: CustomEventTypeInput) =>
      isNew ? CustomEventTypesApi.create(id, input) : CustomEventTypesApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['custom-event', id], data)
      queryClient.invalidateQueries({ queryKey: ['custom-events'] })
      markClean()
      if (isNew) navigate(`/admin/custom-events/${id}`, { replace: true })
      else stopEditing()
      toast.success(t('common.saved'), {
        description: isNew ? t('customEvents.createdSuccess') : t('customEvents.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('customEvents.createFailed')
            : t('customEvents.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const examples = customEventExampleCodes(id)

  const crumbs = [
    { label: t('customEvents.title'), href: '/admin/custom-events' },
    { label: isNew ? t('customEvents.newCustomEvent') : (customEvent?.name ?? t('customEvents.editTitle')) },
  ]

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.customEvent')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={customEvent?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.customEvent')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('customEvents.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={customEvent!.objectState} onChange={saveObjectState} />
              {!isEditing && (
                <Button type="button" variant="outline" onClick={startEditing}>
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              )}
            </div>
          )}
        </div>

        {customEvent && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={customEvent.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(customEvent.createTime)}
                    updated={formatDate(customEvent.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Zap} />
                  {t('customEvents.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="field-grid">
                    <div className="p-field full">
                      <FieldLabel>{t('common.description')}</FieldLabel>
                      <form.Field name="description">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('customEvents.descriptionPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('customEvents.valueTypeHelp')}>
                        {t('customEvents.valueType')}
                      </FieldLabel>
                      <form.Field
                        name="valueType"
                        validators={{
                          onSubmit: ({ value }) => (value ? undefined : t('customEvents.valueTypeRequired')),
                        }}
                      >
                        {(field) => (
                          <>
                            <Select
                              label={t('customEvents.valueType')}
                              value={field.state.value}
                              onValueChange={(v) => field.handleChange(v as CustomEventValueType)}
                            >
                              <SelectTrigger aria-invalid={field.state.meta.errors.length > 0}>
                                <SelectValue placeholder={t('customEvents.selectValueType')}>
                                  {field.state.value ? t(`customEvents.valueTypes.${field.state.value}`) : null}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {CUSTOM_EVENT_VALUE_TYPES.map((v) => (
                                  <SelectItem key={v} value={v}>
                                    {t(`customEvents.valueTypes.${v}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {field.state.meta.errors[0] && (
                              <p className="text-xs text-destructive">{field.state.meta.errors[0] as string}</p>
                            )}
                          </>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('customEvents.displayTypeHelp')}>
                        {t('customEvents.displayType')}
                      </FieldLabel>
                      <form.Field name="type">
                        {(field) => (
                          <Select
                            label={t('customEvents.displayType')}
                            value={field.state.value}
                            onValueChange={(v) => field.handleChange(v as CustomEventDisplayType)}
                          >
                            <SelectTrigger>
                              <SelectValue>{t(`customEvents.displayTypes.${field.state.value}`)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOM_EVENT_DISPLAY_TYPES.map((v) => (
                                <SelectItem key={v} value={v}>
                                  {t(`customEvents.displayTypes.${v}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('customEvents.conversionTypeHelp')}>
                        {t('customEvents.conversionType')}
                      </FieldLabel>
                      <form.Field name="conversionType">
                        {(field) => (
                          <Select
                            label={t('customEvents.conversionType')}
                            value={field.state.value ?? '__clear__'}
                            onValueChange={(v) =>
                              field.handleChange(v === '__clear__' ? null : (v as CustomEventConversionType))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {field.state.value
                                  ? t(`customEvents.conversionTypes.${field.state.value}`)
                                  : t('customEvents.notApplicable')}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__clear__">{t('customEvents.notApplicable')}</SelectItem>
                              {CUSTOM_EVENT_CONVERSION_TYPES.map((v) => (
                                <SelectItem key={v} value={v}>
                                  {t(`customEvents.conversionTypes.${v}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('customEvents.priorityHelp')} hint={t('customEvents.priorityHint')}>
                        {t('customEvents.priority')}
                      </FieldLabel>
                      <form.Field name="priority">
                        {(field) => (
                          <NumberInput
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            min={0}
                            step={1}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                ) : (
                  <DetailList>
                    <DetailRow label={t('common.description')}>{customEvent?.description}</DetailRow>
                    <DetailRow label={t('customEvents.valueType')}>
                      {customEvent?.valueType ? t(`customEvents.valueTypes.${customEvent.valueType}`) : null}
                    </DetailRow>
                    <DetailRow label={t('customEvents.displayType')}>
                      {customEvent ? t(`customEvents.displayTypes.${customEvent.type}`) : null}
                    </DetailRow>
                    <DetailRow label={t('customEvents.conversionType')}>
                      {customEvent?.conversionType
                        ? t(`customEvents.conversionTypes.${customEvent.conversionType}`)
                        : t('customEvents.notApplicable')}
                    </DetailRow>
                    <DetailRow label={t('customEvents.priority')}>
                      {customEvent?.priority ? String(customEvent.priority) : null}
                    </DetailRow>
                  </DetailList>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Code} />
                  {t('customEvents.exampleCode')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4">
                  <p className="text-xs text-muted-foreground">{t('customEvents.exampleCodeHelp')}</p>
                  <div className="p-field">
                    <FieldLabel>{t('customEvents.exampleSimple')}</FieldLabel>
                    <CopyableCode code={examples.simple} />
                  </div>
                  <div className="p-field">
                    <FieldLabel>{t('customEvents.exampleWithValue')}</FieldLabel>
                    <CopyableCode code={examples.withValue} />
                  </div>
                  <div className="p-field">
                    <FieldLabel>{t('customEvents.exampleMultiple')}</FieldLabel>
                    <CopyableCode code={examples.multiple} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div
          className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (isNew ? navigate('/admin/custom-events') : stopEditing())}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('customEvents.newCustomEvent') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function CustomEventEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: customEvent,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['custom-event', id],
    queryFn: () => CustomEventTypesApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('customEvents.title'), href: '/admin/custom-events' },
    { label: isNew ? t('customEvents.newCustomEvent') : t('customEvents.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <CustomEventEditForm key={id} customEvent={customEvent ?? null} id={id!} isNew={isNew} />
}
